import { zStoryRepresentation } from "./storyRepresentations";
import { zStageRuntimeCue } from "./stageRuntime";
import { zStoryFraming } from "./storyFraming";
import { z } from "zod";
import {
  castMemberChoreos,
  zCastChoreos,
  zPerformanceId,
  zStoryReadingLevel,
  type StoryReadingLevel,
} from "./performances";
import { zShowImage, type ShowCharacterChoice } from "./showCast";
import { zImageReference } from "./imageReferenceSchema";
import { zBackdrop } from "./backdrops";
import { zStoryItem } from "./storyItems";

export const zShowCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}$/);
export const zShowRole = z.enum(["stage", "character"]);
export type ShowRole = z.infer<typeof zShowRole>;
export const zShowMotion = z.enum(["giggle", "bounce", "nod", "headshake", "wiggle"]);
export type ShowMotion = z.infer<typeof zShowMotion>;

/** Project nested stage data rather than copying authoring passthrough/private fields. */
function stageProjection(schema: z.ZodTypeAny): z.ZodTypeAny {
  if (schema === zImageReference) return zShowImage;
  if (schema instanceof z.ZodEffects) {
    const projected = stageProjection(schema.innerType());
    return schema._def.effect.type === "refinement" ? projected.superRefine(schema._def.effect.refinement) : projected;
  }
  if (schema instanceof z.ZodObject)
    return z.object(
      Object.fromEntries(
        Object.entries(schema.shape)
          .filter(([key]) => key !== "catalogItemId" && key !== "catalogAccountId" && key !== "policy")
          .map(([key, value]) => [key, stageProjection(value as z.ZodTypeAny)])
      )
    );
  if (schema instanceof z.ZodArray) return z.array(stageProjection(schema.element));
  if (schema instanceof z.ZodDiscriminatedUnion)
    return z.union(schema.options.map(stageProjection) as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]);
  if (schema instanceof z.ZodUnion)
    return z.union(schema.options.map(stageProjection) as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]);
  if (schema instanceof z.ZodOptional) return stageProjection(schema.unwrap()).optional();
  if (schema instanceof z.ZodNullable) return stageProjection(schema.unwrap()).nullable();
  if (schema instanceof z.ZodDefault)
    return stageProjection(schema.removeDefault()).default(schema._def.defaultValue());
  if (schema instanceof z.ZodRecord) return z.record(schema.keySchema, stageProjection(schema.valueSchema));
  return schema;
}
const cueSchemas = zStageRuntimeCue.options as z.AnyZodObject[];
const cue = (kind: string) => cueSchemas.find((schema) => schema.shape.kind.value === kind)!;
const zShowStep = stageProjection(
  z.union(
    cueSchemas.map((schema) =>
      schema.extend({ start: z.enum(["with-previous", "after-previous"]).optional() })
    ) as unknown as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]
  )
);
const zShowPlacement = stageProjection(
  cue("enter").omit({ kind: true, start: true, delayStart: true, duration: true, note: true, manner: true })
);
const zShowCamera = stageProjection(
  cue("camera").omit({ kind: true, start: true, delayStart: true, duration: true, note: true })
);
export const zShowScene = z.object({
  id: zPerformanceId,
  act: z.string().max(200),
  title: z.string().max(300),
  backdrop: z.string().max(200),
  backdropNote: z.string().max(1000).optional(),
  transition: z.enum(["continue", "blackout", "curtain", "shift-up", "shift-down"]).optional(),
  travellers: z.array(zPerformanceId).max(60).optional(),
  castAtStart: z.array(zShowPlacement).max(60),
  itemsAtStart: z.array(stageProjection(zStoryItem)).max(200).optional(),
  camera: zShowCamera.optional(),
  framing: zStoryFraming.optional(),
  representations: z
    .array(zStoryRepresentation.extend({ localId: zPerformanceId.optional() }))
    .max(12)
    .optional(),
  setup: z.array(zShowStep).max(1000).optional(),
  steps: z.array(zShowStep).max(2000),
});
export const zShowCharacter = z
  .object({
    id: zPerformanceId,
    jsonName: z.string().max(200),
    animalId: z.union([zPerformanceId, z.literal("")]).default(""),
    personaId: zPerformanceId.optional(),
    unassigned: z.boolean().optional(),
    displayName: z.string().max(200),
    color: z.string().max(100),
    idlePose: z.string().max(30),
    speakPose: z.string().max(30),
    inventoryContainer: z.string().min(1).max(100).optional(),
    variants: z.record(z.string().max(200)).optional(),
    variantPersonaIds: z.record(zPerformanceId).optional(),
    /**
     * The role's own named sequences, so a shared stage plays one the stager named by id. An entry that does not
     * parse is left out rather than failing the share, and step notes, which only the stager reads, stay behind.
     */
    choreos: z.preprocess((value) => {
      const choreos = castMemberChoreos({ choreos: value });
      return choreos.length
        ? choreos.map((choreo) => ({ ...choreo, poses: choreo.poses.map(({ description: _notes, ...step }) => step) }))
        : undefined;
    }, zCastChoreos.optional()),
  })
  .superRefine((role, ctx) => {
    if (role.unassigned === true) {
      if (role.animalId || role.jsonName || role.personaId || Object.keys(role.variantPersonaIds ?? {}).length)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "An empty cast slot cannot reference a character." });
    } else if (!role.animalId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A cast slot without a character must be marked unassigned.",
      });
    }
  });
const finite = z.number().finite();
const zShowBackdrop = zBackdrop.extend({
  image: zShowImage.optional(),
  imageVariations: z.record(zShowImage).optional(),
});
const numericSettingNames = [
  "featuredScale",
  "autoplaySpeed",
  "reactionSpeed",
  "readBaseMs",
  "readPerCharMs",
  "readMaxMs",
  "rimOverlap",
  "backdropTop",
  "curtainMs",
  "houseFadeMs",
  "shiftMs",
  "textScale",
  "narratorFrameScale",
  "tightOffsetPx",
  "bubbleMinWidth",
  "bubbleMoveMs",
  "spotConeBrightness",
  "spotConeWidth",
  "spotConeSoftness",
  "spotConeHeight",
  "spotConeAngle",
  "spotMoveMs",
  "speakerGlowSize",
  "speakerGlowIntensity",
  "frameWidth",
  "frameShadowSize",
  "frameShadowStrength",
  "effectScale",
  "effectDwellMs",
] as const;
const booleanSettingNames = [
  "showHints",
  "tightBubbles",
  "speakerGlowEnabled",
  "panArrows",
  "frameShown",
  "frameAuto",
  "showSlotBoxes",
  "panelOpen",
  "sfxHints",
  "poseHeightTransitions",
] as const;
export const zShowSettings = z.object({
  narrationMode: z.enum(["script", "stage", "director"]).optional(),
  ...Object.fromEntries(numericSettingNames.map((key) => [key, finite.min(-1000).max(120000).optional()])),
  reactionSpeed: finite.min(0.25).max(3).optional(),
  ...Object.fromEntries(booleanSettingNames.map((key) => [key, z.boolean().optional()])),
  stepModes: z.record(z.enum(["on-next", "auto", "skip", "with-previous", "with-next"])).optional(),
  autoDelayMs: z.record(finite.min(0).max(60000)).optional(),
  locationScale: z.record(finite.min(0.01).max(20)).optional(),
  onCurtainClose: z.enum(["leave", "exit", "peek"]).optional(),
  spotConeColor: z.string().max(100).optional(),
  speakerGlowColor: z.string().max(100).optional(),
  panControls: z.enum(["hidden", "resume", "full"]).optional(),
});
export const zShowProgramInput = z.object({
  storyId: zPerformanceId,
  title: z.string().max(300),
  draftWatermark: z.boolean().optional(),
  characters: z.array(zShowCharacter).max(100),
  backdrops: z.record(zShowBackdrop),
  script: z.object({ title: z.string().max(300), scenes: z.array(zShowScene).min(1).max(12) }),
  stream: z.object({
    id: z
      .string()
      .min(1)
      .max(200)
      .regex(/^[a-zA-Z0-9_:.-]+$/),
    complete: z.boolean(),
  }),
  interlude: z.boolean().optional(),
  /** A bounded return point; never treat the temporary cast-break script as saved story content. */
  interludeOrigin: z
    .object({
      sceneId: zPerformanceId,
      step: z.number().int().min(0).max(2000),
      pauseRule: z.enum(["never", "speech", "speech-scene", "roles", "scene", "every"]),
      onCurtainClose: z.enum(["leave", "exit", "peek"]).optional(),
    })
    .optional(),
  startingStep: z.number().int().min(0).max(2000).optional(),
  pauseRule: z.enum(["never", "speech", "speech-scene", "roles", "scene", "every"]).optional(),
  roles: z.array(zPerformanceId).max(100).optional(),
  settings: zShowSettings.optional(),
  /** Current, individually addressable clips; authoring voice prompts stay private. */
  speechAudio: z
    .record(
      zPerformanceId,
      z.record(
        z.string().regex(/^\d+$/),
        z.object({
          characterId: zPerformanceId,
          text: z.string().max(20000),
          url: z.string().url().max(4000),
          durationMs: finite.positive().max(600000),
          sourceKey: z.string().max(200),
        })
      )
    )
    .optional(),
});
export type ShowProgramInput = z.infer<typeof zShowProgramInput>;
export type ShowSpeechAudio = NonNullable<ShowProgramInput["speechAudio"]>;

export const zShowSpeechReadiness = z.object({
  revision: z.number().int().nonnegative(),
  routingRevision: z.number().int().nonnegative().optional(),
  state: z.enum(["ready", "loading", "blocked", "unavailable"]),
});
export type ShowSpeechReadiness = z.infer<typeof zShowSpeechReadiness>;
export const zShowSpeechRoute = z.discriminatedUnion("output", [
  z.object({ output: z.literal("off") }).strict(),
  z.object({ output: z.literal("stage") }).strict(),
  z
    .object({
      output: z.literal("device"),
      deviceUid: z
        .string()
        .min(1)
        .max(128)
        .refine((value) => !value.includes("/")),
    })
    .strict(),
]);
export type ShowSpeechRoute = z.infer<typeof zShowSpeechRoute>;
export interface ShowSpeechSettings {
  enabled: boolean;
  /** Changes to output selection invalidate acknowledgments captured for an older route. */
  routingRevision?: number;
  /** Free system voice covers lines without a generated character clip. */
  deviceFallback?: boolean;
  /** Missing entries follow the cast: performer off, other characters on stage. */
  routes: Record<string, ShowSpeechRoute>;
  /** One concrete receiver per character prevents a director, TV and iframe echo. */
  targets: Record<string, string | null>;
}
export type ShowProgram = ShowProgramInput & {
  revision: number;
  epoch: number;
  updatedAt: number;
  castRevision?: number;
};
export const zShowTransportInput = z.object({
  programRevision: z.number().int().positive(),
  sceneId: zPerformanceId,
  step: z.number().int().min(0).max(2000),
  playing: z.boolean(),
  elapsedMs: z.number().min(0).max(600000).default(0),
  waiting: z.enum(["viewer", "stream"]).optional(),
  /** The displayed line whose performer may release this viewer hold. */
  performerTurn: z.object({ characterId: zPerformanceId, lineStep: z.number().int().min(0).max(2000) }).optional(),
  tempo: z.number().min(0.1).max(8).optional(),
});
export type ShowTransportInput = z.input<typeof zShowTransportInput>;
export type ShowTransport = z.output<typeof zShowTransportInput> & {
  revision: number;
  epoch: number;
  updatedAt: number;
};
export interface ShowSession {
  id: string;
  ownerUid: string;
  title: string;
  status: "active" | "ended";
  epoch: number;
  createdAt: number;
  updatedAt: number;
  expiresAt: number;
  speech?: ShowSpeechSettings;
}

/** Resolve logical screens to their active embedded receiver and elect one main stage. */
export function resolveShowSpeechTargets(
  settings: Pick<ShowSpeechSettings, "enabled" | "routes">,
  characters: readonly { id: string }[],
  members: readonly ShowMember[],
  ownerUid: string,
  now: number
): Record<string, string | null> {
  const live = members.filter((member) => !member.revoked && member.expiresAt > now);
  const fresh = (member: ShowMember) =>
    member.visibility !== "hidden" && now - (member.lastSeenAt ?? member.joinedAt) < 90000;
  const logical = live.filter((member) => !member.handoffSourceUid);
  const receiver = (source: ShowMember | undefined) => {
    if (!source) return null;
    const embedded = live.filter(
      (member) => member.handoffSourceUid === source.uid && member.role === source.role && fresh(member)
    );
    embedded.sort((a, b) => (b.lastSeenAt ?? b.joinedAt) - (a.lastSeenAt ?? a.joinedAt) || a.uid.localeCompare(b.uid));
    return embedded[0]?.uid ?? (fresh(source) ? source.uid : null);
  };
  const stages = logical.filter((member) => member.role === "stage" && receiver(member));
  stages.sort((a, b) => a.joinedAt - b.joinedAt || a.uid.localeCompare(b.uid));
  const mainStage = receiver(stages[0]) ?? receiver(logical.find((member) => member.uid === ownerUid));
  return Object.fromEntries(
    [...characters.map((character) => character.id), "narrator"].map((id) => {
      const assigned = logical.some(
        (member) =>
          member.role === "character" &&
          (
            member.effectiveCharacterIds ??
            member.characterIds ??
            (member.characterId ? [member.characterId] : [])
          ).includes(id)
      );
      const route = settings.routes[id] ?? { output: assigned ? "off" : "stage" };
      const deviceUid = route.output === "device" ? route.deviceUid : undefined;
      const target =
        !settings.enabled || route.output === "off"
          ? null
          : route.output === "stage"
            ? mainStage
            : receiver(logical.find((member) => member.uid === deviceUid));
      return [id, target];
    })
  );
}
export interface ShowCharacterAssignment {
  /** First explicit character, retained for older clients. */
  characterId?: string;
  characterIds?: string[];
  /** Automatically cover characters without another explicit performer. */
  ensemble?: boolean;
}
/** A small verified projection of withAuth's userAgent, never its private profile. */
export interface ShowPerformerUserAgent {
  name: string;
  userId: string;
  accountId?: string;
  mindId?: string;
  mindPath?: string;
}
export interface ShowPerformerIdentity {
  /** The performer's editable, session-specific introduction. */
  name: string;
  userAgent?: ShowPerformerUserAgent;
}
/** Shared across the director and this performer's screen, independent of device capabilities. */
export const zShowPerformerSettings = z
  .object({
    autoRead: z.boolean(),
    vibrateOnTurn: z.boolean(),
    showDoneButton: z.boolean(),
    readingLevel: zStoryReadingLevel,
    speechRate: z.number().finite().min(0.5).max(1.5),
    chunkedSpeech: z.boolean(),
    wordsPerChunk: z.number().int().min(1).max(20),
    pauseMultiplier: z.number().finite().min(0).max(3),
    pauseExtraSeconds: z.number().finite().min(0).max(10),
  })
  .strict();
export type ShowPerformerSettings = z.infer<typeof zShowPerformerSettings>;
export const zShowPerformerSettingsPatch = zShowPerformerSettings
  .partial()
  .refine(
    (value) => Object.values(value).some((setting) => setting !== undefined),
    "Choose at least one performer setting"
  );
export const SHOW_PERFORMER_SETTINGS_DEFAULTS: Readonly<ShowPerformerSettings> = {
  autoRead: false,
  vibrateOnTurn: false,
  showDoneButton: false,
  readingLevel: "original",
  speechRate: 1,
  chunkedSpeech: false,
  wordsPerChunk: 4,
  pauseMultiplier: 1,
  pauseExtraSeconds: 0,
};
export function resolveShowPerformerSettings(
  member: Pick<ShowMember, "performerSettings" | "readingLevel">
): ShowPerformerSettings {
  return {
    ...SHOW_PERFORMER_SETTINGS_DEFAULTS,
    ...member.performerSettings,
    readingLevel: member.readingLevel ?? member.performerSettings?.readingLevel ?? "original",
  };
}
export interface ShowMember extends ShowCharacterAssignment {
  uid: string;
  role: "director" | ShowRole;
  /** Current resolved cast, including dynamic ensemble assignments. */
  effectiveCharacterIds?: string[];
  lastSeenAt?: number;
  visibility?: "visible" | "hidden";
  label?: string;
  /** Concrete receiver capability; absent on older clients that do not report voice readiness. */
  speechReadiness?: ShowSpeechReadiness;
  performer?: ShowPerformerIdentity;
  readingLevel?: StoryReadingLevel;
  performerSettings?: ShowPerformerSettings;
  performerSettingsRevision?: number;
  /** Originating invitation, for the director to recover its share link. */
  inviteCode?: string;
  joinedAt: number;
  expiresAt: number;
  revoked?: boolean;
  /** Embedded receivers remain dependent on the originating screen's membership. */
  handoffSourceUid?: string;
}

/** Initial/resumed Play waits only for modern receivers that actually have speech to deliver. */
export function showSpeechReadinessBlockers(
  program: Pick<ShowProgram, "revision" | "script" | "settings" | "speechAudio">,
  speech: ShowSpeechSettings | undefined,
  members: readonly ShowMember[]
): Array<{ uid: string; label: string; state: Exclude<ShowSpeechReadiness["state"], "ready"> }> {
  if (!speech?.enabled) return [];
  const targets = new Set<string>();
  for (const scene of program.script.scenes)
    for (const [index, step] of scene.steps.entries()) {
      if (step.kind !== "line" && step.kind !== "narrator") continue;
      if (
        step.kind === "narrator" &&
        (program.settings?.narrationMode === "director" ||
          ((program.settings?.narrationMode ?? "script") === "script" && step.delivery === "director"))
      )
        continue;
      const characterId = step.kind === "line" ? step.character || "narrator" : "narrator";
      const clip = program.speechAudio?.[scene.id]?.[index];
      if (!speech.deviceFallback && (!clip || clip.text !== step.text || clip.characterId !== characterId)) continue;
      const target = speech.targets[characterId];
      if (target) targets.add(target);
    }
  return [...targets].flatMap((uid) => {
    const member = members.find((entry) => entry.uid === uid);
    const capability = member?.speechReadiness;
    const current =
      capability?.revision === program.revision && (capability.routingRevision ?? 0) === (speech.routingRevision ?? 0);
    if (!capability || (current && capability.state === "ready")) return [];
    return [
      {
        uid,
        label:
          member?.performer?.name ||
          member?.label ||
          (member?.role === "stage"
            ? "Stage screen"
            : member?.role === "director"
              ? "Director device"
              : "Performer device"),
        state: current ? (capability.state as Exclude<ShowSpeechReadiness["state"], "ready">) : ("loading" as const),
      },
    ];
  });
}
export interface ShowHandoff {
  /** Short-lived bearer ticket: transfer to the app origin in a fragment and consume immediately. */
  ticket: string;
  expiresAt: number;
}
export interface ShowPairing extends ShowCharacterAssignment {
  id: string;
  uid: string;
  code: string;
  expiresAt: number;
  sessionId?: string;
  role?: ShowRole;
  characterId?: string;
  claimed?: boolean;
  supportsSpeechReadiness?: boolean;
}
export interface ShowInvite extends ShowCharacterAssignment {
  code: string;
  sessionId: string;
  role: ShowRole | "choose";
  characterId?: string;
  expiresAt: number;
  revoked?: boolean;
}
export interface ShowCue {
  id: string;
  revision: number;
  programRevision: number;
  epoch: number;
  characterId: string;
  motion: ShowMotion;
  uid: string;
  updatedAt: number;
}
/** One performer releases one exact published viewer hold; only the director advances playback. */
export interface ShowTurnCompletion {
  id: string;
  programRevision: number;
  transportRevision: number;
  epoch: number;
  sceneId: string;
  lineStep: number;
  characterId: string;
  uid: string;
  updatedAt: number;
}
export type ManageShowSessionRequest =
  | { action: "indexSessions"; after?: string }
  | { action: "create"; accountId: string; title?: string; supportsSpeechReadiness?: boolean }
  | { action: "createPairing"; supportsSpeechReadiness?: boolean }
  | ({ action: "attachPairing"; sessionId: string; code: string; role: ShowRole } & ShowCharacterAssignment)
  | ({ action: "createInvite"; sessionId: string; role: ShowRole | "choose" } & ShowCharacterAssignment)
  | { action: "listInvites"; sessionId: string }
  | ({ action: "updateMember"; sessionId: string; uid: string; role: ShowRole } & ShowCharacterAssignment)
  | {
      action: "heartbeat";
      sessionId: string;
      visibility: "visible" | "hidden";
      label?: string;
      speechReadiness?: ShowSpeechReadiness;
    }
  | {
      action: "setSpeech";
      sessionId: string;
      enabled?: boolean;
      deviceFallback?: boolean;
      characterId?: string;
      route?: ShowSpeechRoute | null;
    }
  | {
      action: "createPerformerIdentity";
      sessionId: string;
      screenUid: string;
      userAgent: { name: string; userId: string; mindPath?: string };
    }
  | { action: "identifyPerformer"; sessionId: string; name: string; identityTicket?: string }
  | { action: "setReadingLevel"; sessionId: string; readingLevel: StoryReadingLevel; uid?: string }
  | {
      action: "setPerformerSettings";
      sessionId: string;
      uid?: string;
      settings: Partial<ShowPerformerSettings>;
      /** Seed a legacy device only if no screen settings have been saved yet. */
      initialOnly?: boolean;
    }
  | ({ action: "join"; code: string; role?: ShowRole; supportsSpeechReadiness?: boolean } & ShowCharacterAssignment)
  | { action: "createHandoff"; sessionId: string; supportsSpeechReadiness?: boolean }
  | { action: "redeemHandoff"; ticket: string; supportsSpeechReadiness?: boolean }
  | {
      action: "publish";
      sessionId: string;
      expectedRevision: number;
      program: ShowProgramInput;
      source?: { accountId: string; performanceId: string };
    }
  | { action: "transport"; sessionId: string; expectedRevision: number; transport: ShowTransportInput }
  | { action: "cue"; sessionId: string; programRevision: number; characterId: string; motion: ShowMotion }
  | {
      action: "completeTurn";
      sessionId: string;
      programRevision: number;
      transportRevision: number;
      characterId: string;
      lineStep: number;
    }
  | { action: "revoke"; sessionId: string; uid?: string; code?: string }
  | { action: "end"; sessionId: string };
export interface ManageShowSessionResponse {
  index?: { scanned: number; indexed: number; nextCursor?: string };
  session?: ShowSession;
  sessionId?: string;
  member?: ShowMember;
  pairing?: ShowPairing;
  invite?: ShowInvite;
  invites?: ShowInvite[];
  handoff?: ShowHandoff;
  /** One-use, screen-bound identity ticket. Never put this in URLs or persistent storage. */
  performerIdentityTicket?: string;
  program?: ShowProgram;
  transport?: ShowTransport;
  cue?: ShowCue;
  turnCompletion?: ShowTurnCompletion;
  chooseRequired?: boolean;
  availableCharacters?: ShowCharacterChoice[];
  revoked?: boolean;
  ended?: boolean;
}
